import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { ActivityEntityName, VisitApprovalValidityState, VisitApprovalVisitRow } from '@jms/model';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { DateTime } from 'luxon';
import { OutlineRow, OutlineRowRendererComponent } from '@workbuddy/surfaces/outline-authoring';
import { BadgeMode } from '@workbuddy/ui/components/badge';

/** Adapts one approved visit to the shared outline row without inventing a recurring schedule. */
@Component({
  selector: 'wb-approval-entry-row',
  imports: [OutlineRowRendererComponent],
  template: '<outline-row [presentationRow]="row()" [presentationIcon]="null" [reserveTreeGutter]="false" />',
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApprovalEntryRowComponent implements ICellRendererAngularComp {
  private readonly approved = signal<VisitApprovalVisitRow | undefined>(undefined);
  readonly row = computed(() => toApprovalEntryOutlineRow(this.approved()));

  agInit(params: ICellRendererParams<VisitApprovalVisitRow>): void {
    this.approved.set(params.data);
  }

  refresh(params: ICellRendererParams<VisitApprovalVisitRow>): boolean {
    this.approved.set(params.data);
    return true;
  }
}

/**
 * The label other outline chips already follow (schedule chip, `visitApprovalProgressBadge`): one short
 * word in the badge, the state and its reason only in the tooltip. `approved.approvalState` and
 * `approvalStateReason` can run to a full sentence, and a badge chip has no end-ellipsis of its own —
 * `w-badge` centres its label, so an overflowing string clips at BOTH ends instead of truncating cleanly.
 */
const APPROVAL_EXCEPTION_LABEL = 'Changed';

/** The saved appointment stays primary; a state other than Approved is the one exception badge. */
export function toApprovalEntryOutlineRow(approved: VisitApprovalVisitRow | undefined): OutlineRow | undefined {
  if (!approved) return undefined;
  const date = DateTime.fromJSDate(new Date(approved.startDate), { zone: approved.timezone });
  const hasException = approved.approvalState && approved.approvalState !== VisitApprovalValidityState.Approved;
  const exceptionDetail = [approved.approvalState, approved.approvalStateReason].filter(Boolean).join(' · ');
  return {
    _id: approved._id,
    rowId: approved._id,
    sourceId: approved.occurrenceId,
    sourceEntity: ActivityEntityName.Visit,
    name: approved.name,
    sortOrder: '',
    hasChildren: false,
    badges: [
      ...(hasException ? [{ id: 'validity', label: APPROVAL_EXCEPTION_LABEL, tooltip: exceptionDetail }] : []),
      { id: 'appointment', label: date.toFormat('d MMM, h:mm a'), mode: BadgeMode.Item, iconLeft: 'fa-regular fa-calendar', tooltip: [date.toFormat('d MMM yyyy, h:mm a ZZZZ'), approved.site?.name].filter(Boolean).join(' · ') },
    ],
  };
}
